import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { useParams } from "react-router-dom";

export interface RegulationDocument {
  id: string;
  name: string;
  pdfFileName: string | null;
  parentId: string | null;
}

const regulationsDemo: Record<string, RegulationDocument> = {
  PERG: {
    id: "PERG",
    pdfFileName: "PERG.pdf",
    name: "PERG",
    parentId: null,
  },
  COBS2: {
    id: "COBS2",
    pdfFileName: "COBS2.pdf",
    name: "COBS 2",
    parentId: "COBS",
  },
  COBS3: {
    id: "COBS3",
    pdfFileName: "COBS3.pdf",
    name: "COBS 3",
    parentId: "COBS",
  },
  COBS4: {
    id: "COBS4",
    pdfFileName: "COBS4.pdf",
    name: "COBS 4",
    parentId: "COBS",
  },
  COBS5: {
    id: "COBS5",
    pdfFileName: "COBS5.pdf",
    name: "COBS 5",
    parentId: "COBS",
  },
  COBS6: {
    id: "COBS6",
    pdfFileName: "COBS6.pdf",
    name: "COBS 6",
    parentId: "COBS",
  },
  COBS9: {
    id: "COBS9",
    pdfFileName: "COBS9.pdf",
    name: "COBS 9",
    parentId: "COBS",
  },
  "cp25-17": {
    id: "cp25-17",
    pdfFileName: "cp25-17.pdf",
    name: "CP25/17",
    parentId: null,
  },
  "ps25-22": {
    id: "ps25-22",
    pdfFileName: "ps25-22.pdf",
    name: "PS25/22",
    parentId: null,
  },
};

interface RegulationsProviderContext {
  regulation: RegulationDocument | undefined;
  regulations: RegulationDocument[];
  setRegulation: (regulation?: RegulationDocument) => void;
}

const RegulationsProviderContext = createContext<RegulationsProviderContext>({
  regulations: [],
  regulation: undefined,
  setRegulation: () => null,
});

export const RegulationsProvider = ({ children }: PropsWithChildren) => {
  const { regulationId } = useParams<{ regulationId: string }>();
  const [regulation, setRegulation] =
    useState<RegulationsProviderContext["regulation"]>();

  useEffect(() => {
    if (!regulationId) return;
    setRegulation(regulationsDemo[regulationId] ?? {
      // Dynamic regulation that's fetched entirely from the backend. All the fields are just `regulationId` because:
      // id: well, regulationId is indeed the id!
      // name: what we're displaying needs a name. It's shown in the UI. In the old UI, it would be something like
      //   "COBS 9". Longer term, we probably want to fetch a name from the backend, but this is fine for now.
      // pdfFileName: Used to fetch highlights from the API, and the PDF itself. In the old API, it would be something
      //   like "COBS9.pdf", but the new backend requires simply the id when fetching both highlights and the PDF.
      id: regulationId,
      name: regulationId,
      pdfFileName: regulationId,
    });
  }, [regulationId]);

  useEffect(() => {
    if (!regulation) return;
    // Update URL without triggering a re-render
    window.history.pushState({}, "", `/regulations/${regulation.id}`);
  }, [regulation]);

  return (
    <RegulationsProviderContext.Provider
      value={{
        regulations: Object.values(regulationsDemo),
        regulation,
        setRegulation,
      }}
    >
      {children}
    </RegulationsProviderContext.Provider>
  );
};

export const useRegulations = () => {
  return useContext(RegulationsProviderContext);
};
